import React, { useCallback, useEffect, useRef, useState } from 'react';
import GradebookPng from '../assets/png/gradebook.png';
import '../styles/gradebook-transition.css';

interface OriginRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TransitionState {
  active: boolean;
  direction: 'open' | 'close';
  origin: OriginRect | null;
}

let globalTrigger: ((direction: 'open' | 'close', origin?: OriginRect) => void) | null = null;
let globalOnDone: (() => void) | null = null;
let lastOrigin: OriginRect | null = null;

/**
 * Fire the gradebook book-zoom animation.
 * For 'open', pass the bounding rect of the clicked book image.
 * For 'close', the last open origin is reused automatically.
 */
export function triggerGradebookTransition(
  direction: 'open' | 'close',
  origin?: OriginRect,
): Promise<void> {
  return new Promise((resolve) => {
    globalOnDone = resolve;
    if (direction === 'open' && origin) {
      lastOrigin = origin;
    }
    globalTrigger?.(direction, direction === 'open' ? origin : lastOrigin ?? undefined);
  });
}

const ANIMATION_DURATION = 900;

const GradebookTransition: React.FC = () => {
  const [state, setState] = useState<TransitionState>({ active: false, direction: 'open', origin: null });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trigger = useCallback((direction: 'open' | 'close', origin?: OriginRect) => {
    setState({ active: true, direction, origin: origin ?? null });
  }, []);

  useEffect(() => {
    globalTrigger = trigger;
    return () => { globalTrigger = null; };
  }, [trigger]);

  useEffect(() => {
    if (!state.active) return;
    document.body.classList.add('gb-transition-active');
    timerRef.current = setTimeout(() => {
      document.body.classList.remove('gb-transition-active');
      setState({ active: false, direction: 'open', origin: null });
      globalOnDone?.();
      globalOnDone = null;
    }, ANIMATION_DURATION);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.body.classList.remove('gb-transition-active');
    };
  }, [state.active]);

  if (!state.active) return null;

  const animClass = state.direction === 'open'
    ? 'gradebook-transition-zoom-in'
    : 'gradebook-transition-zoom-out';

  // Compute CSS custom properties for the origin position
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const o = state.origin;
  const originX = o ? o.left + o.width / 2 : vw / 2;
  const originY = o ? o.top + o.height / 2 : vh / 2;
  const bookWidth = o ? o.width : 160;

  const style = {
    '--gb-origin-x': `${originX}px`,
    '--gb-origin-y': `${originY}px`,
    '--gb-book-w': `${bookWidth}px`,
    // How much to translate from center to origin
    '--gb-tx': `${originX - vw / 2}px`,
    '--gb-ty': `${originY - vh / 2}px`,
  } as React.CSSProperties;

  return (
    <div className={`gradebook-transition-overlay ${animClass}`} style={style}>
      <div className="gradebook-transition-book">
        <img src={GradebookPng} alt="" />
      </div>
    </div>
  );
};

export default GradebookTransition;
